import { useState } from "react";
import { Box, CircularProgress, Stack, Typography } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { useClinOpsView } from "@/api/hooks/clinopsView";
import { useForceActivateCohort } from "@/api/hooks/participations";
import { useRemoveSiteDAInvestigator } from "@/api/hooks/siteDAInvestigators";
import { useRemoveLocationFromDA } from "@/api/hooks/siteDALocations";
import { LocationsInDAPanel } from "./LocationsInDAPanel";
import { PharmaStudiesPanel } from "./PharmaStudiesPanel";
import { AddLocationToDADialog } from "./AddLocationToDADialog";
import { AssignInvestigatorDialog } from "./AssignInvestigatorDialog";

interface Props {
  siteId: string;
  daCode: string;
}

export function DiseaseAreaPanel({ siteId, daCode }: Props) {
  const qc = useQueryClient();
  const view = useClinOpsView(siteId, daCode);
  const removeInv = useRemoveSiteDAInvestigator(siteId, daCode);
  const removeLoc = useRemoveLocationFromDA(siteId, daCode);
  const activateCohort = useForceActivateCohort(siteId, daCode);
  const [addLocOpen, setAddLocOpen] = useState(false);
  const [assignRole, setAssignRole] = useState<"pi" | "sub_investigator" | null>(null);

  if (view.isLoading || !view.data) {
    return (
      <Box sx={{ textAlign: "center", py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  const daId = view.data.disease_area.id;

  return (
    <Stack spacing={2} sx={{ pt: 2 }}>
      {/* Hierarchy: TherapeuticArea → (Cohort | Sponsored studies)
       * → Locations → People. The cohort is a single panel — its
       * title bar carries the milestones (always visible) and the
       * body collapses to hide investigators + per-location detail. */}
      <Box>
        <Typography
          variant="overline"
          color="text.secondary"
          sx={{ display: "block", fontWeight: 700, mb: 0.5 }}
        >
          Cohort
        </Typography>
        <LocationsInDAPanel
          masterStudy={view.data.master_study}
          locations={view.data.locations_in_da}
          locationDocuments={view.data.location_documents}
          onAdd={() => setAddLocOpen(true)}
          onRemove={(locationId) => {
            const reason =
              window.prompt("Reason for removing this location?") ?? "";
            if (reason) removeLoc.mutate({ daId, locationId, reason });
          }}
          onActivateCohort={(participationId) =>
            activateCohort.mutate(participationId)
          }
          activatingParticipationId={
            activateCohort.isPending
              ? (activateCohort.variables ?? null)
              : null
          }
          milestones={view.data.milestones}
          pi={view.data.pi}
          subInvestigators={view.data.sub_investigators}
          piDocuments={view.data.pi_documents}
          subInvestigatorDocuments={view.data.sub_investigator_documents}
          onAddSub={() => setAssignRole("sub_investigator")}
          onReplacePI={() => setAssignRole("pi")}
          onRemoveInvestigator={(assignmentId) => {
            const reason =
              window.prompt("Reason for removing this investigator?") ?? "";
            if (reason) removeInv.mutate({ daId, assignmentId, reason });
          }}
        />
      </Box>

      <Box>
        <Typography
          variant="overline"
          color="text.secondary"
          sx={{ display: "block", fontWeight: 700, mb: 0.5 }}
        >
          Sponsored studies
        </Typography>
        <PharmaStudiesPanel
          studies={view.data.pharma_studies}
          onEnroll={(studyId, locationId) => {
            enrollPharmaWrapper({ qc, siteId, daCode, daId, studyId, locationId });
          }}
          onUnenroll={(studyId, locationId) => {
            const reason = window.prompt("Reason for un-enrolling?") ?? "";
            if (reason)
              unenrollPharmaWrapper({
                qc,
                siteId,
                daCode,
                daId,
                studyId,
                locationId,
                reason,
              });
          }}
        />
      </Box>
      <AddLocationToDADialog
        open={addLocOpen}
        onClose={() => setAddLocOpen(false)}
        siteId={siteId}
        daId={daId}
        daCode={daCode}
        alreadyEnrolledIds={new Set(
          view.data.locations_in_da.map((l) => l.location_id),
        )}
      />
      <AssignInvestigatorDialog
        open={assignRole !== null}
        onClose={() => setAssignRole(null)}
        siteId={siteId}
        daId={daId}
        daCode={daCode}
        role={assignRole ?? "pi"}
        hasExistingPI={view.data.pi !== null}
      />
    </Stack>
  );
}

// The pharma hooks are parameterised by studyId; calling them inside a
// .map() would violate the Rules of Hooks. Instead, we call apiRequest
// directly and invalidate via the QueryClient passed from the component.
async function enrollPharmaWrapper(args: {
  qc: ReturnType<typeof useQueryClient>;
  siteId: string;
  daCode: string;
  daId: string;
  studyId: string;
  locationId: string;
}) {
  const { apiRequest } = await import("@/api/client");
  await apiRequest(
    `/api/sites/${args.siteId}/disease-areas/${args.daId}/studies/${args.studyId}/locations/${args.locationId}`,
    { method: "POST" },
  );
  args.qc.invalidateQueries({
    queryKey: ["sites", args.siteId, "clinops", args.daCode],
  });
}

async function unenrollPharmaWrapper(args: {
  qc: ReturnType<typeof useQueryClient>;
  siteId: string;
  daCode: string;
  daId: string;
  studyId: string;
  locationId: string;
  reason: string;
}) {
  const { apiRequest } = await import("@/api/client");
  await apiRequest(
    `/api/sites/${args.siteId}/disease-areas/${args.daId}/studies/${args.studyId}/locations/${args.locationId}`,
    { method: "DELETE", body: JSON.stringify({ reason: args.reason }) },
  );
  args.qc.invalidateQueries({
    queryKey: ["sites", args.siteId, "clinops", args.daCode],
  });
}
